import React, { useState, useEffect } from 'react';
import { pnrFormSchema } from '../pages/pnr/PNRFormSchema';
import { ripFormSchema } from '../pages/rip/RIPFormSchema';
import { trFormSchema } from '../pages/tr/TRFormSchema';
import { tarFormSchema } from '../pages/tar/TARFormSchema';
import { imcsTarFormSchema } from '../pages/tar-imcs/IMCSTARFormSchema';
import { ctpFormSchema } from '../pages/ctp/CTPFormSchema';

// Fallback when schema name is unknown
const blankSchema = [];

const getSchemaForModule = (moduleName) => {
  const schemaMap = {
    PNRFormSchema: pnrFormSchema,
    RIPFormSchema: ripFormSchema,
    TRFormSchema: trFormSchema,
    TARFormSchema: tarFormSchema,
    IMCSTARFormSchema: imcsTarFormSchema,
    CTPFormSchema: ctpFormSchema,
  };
  return schemaMap[moduleName] ?? blankSchema;
};

const buildInitialState = (moduleName) => {
  const schema = getSchemaForModule(moduleName);
  return parseSchemaToInitialState(schema);
};

const getInitialValue = (field) => {
  switch (field.type) {
    case 'text':
    case 'dropdown':
    case 'select':
    case 'conditionalText':
    case 'radio':
    case 'rowselect':
      return field.value || '';
    case 'autocomplete':
      return field.multiple ? [] : (field.value || '');
    case 'date':
    case 'dateRange':
      // Initialize dateRange as an object to match renderer expectations
      return field.type === 'dateRange' ? { start: null, end: null } : null;
    case 'fileUpload':
      return [];
    case 'nestedexpandable':
      if (field.tables) {
        return {
          [field.tables[0].name]: [],
        };
      }
      return {};
    case 'nested':
      return field.fields.reduce(
        (acc, f) => ({
          ...acc,
          [f.name]: getInitialValue(f),
        }),
        {},
      );
    default:
      return '';
  }
};

const parseSchemaToInitialState = (schema) => {
  const initialState = {};

  const processFields = (fields) => {
    if (!Array.isArray(fields)) return initialState;
    fields.forEach((field) => {
      if (field.fields) {
        Object.assign(initialState, processFields(field.fields));
      }
      if (field.name) {
        initialState[field.name] = getInitialValue(field);
      }
    });
    return initialState;
  };

  // Normalize schema: support both array-of-fields and object with blocks
  const normalizeToFields = (sch) => {
    if (Array.isArray(sch)) return sch;
    if (sch && Array.isArray(sch.blocks)) {
      // Flatten blocks' fields
      const out = [];
      sch.blocks.forEach((b) => {
        if (Array.isArray(b?.fields)) out.push(...b.fields);
      });
      return out;
    }
    return [];
  };

  return processFields(normalizeToFields(schema));
};

export default function useSchemaForm(schemaModule) {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const resetFormData = React.useCallback(() => {
    const initialState = buildInitialState(schemaModule);
    setFormData(initialState || {});
  }, [schemaModule]);

  useEffect(() => {
    try {
      setIsLoading(true);
      const initialState = buildInitialState(schemaModule);
      setFormData(initialState || {});
    } catch (error) {
      console.error('Error loading schema:', error);
      setFormData({});
    } finally {
      setIsLoading(false);
    }
  }, [schemaModule]);

  return [formData, setFormData, isLoading, resetFormData];
}
